import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { socialAPI } from '../../utils/api';

// Async thunk для отправки запроса в друзья
export const sendFriendRequest = createAsyncThunk(
  'friends/sendFriendRequest',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await socialAPI.sendFriendRequest(userId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to send friend request');
    }
  }
);

// Async thunk для принятия запроса в друзья
export const acceptFriendRequest = createAsyncThunk(
  'friends/acceptFriendRequest',
  async (userId, { rejectWithValue, getState }) => {
    try {
      const response = await socialAPI.acceptFriendRequest(userId);
      // Получаем ID текущего пользователя (который принял запрос)
      const currentUserId = getState().auth?.user?.id;
      // Возвращаем userId (ID пользователя, отправившего запрос) и currentUserId (ID текущего пользователя)
      return { userId, currentUserId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to accept friend request');
    }
  }
);

// Async thunk для отклонения запроса в друзья
export const rejectFriendRequest = createAsyncThunk(
  'friends/rejectFriendRequest',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await socialAPI.rejectFriendRequest(userId);
      // Возвращаем userId для обновления состояния
      return { userId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to reject friend request');
    }
  }
);

// Async thunk для отмены запроса в друзья
export const cancelFriendRequest = createAsyncThunk(
  'friends/cancelFriendRequest',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await socialAPI.cancelFriendRequest(userId);
      return { userId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to cancel friend request');
    }
  }
);

// Async thunk для удаления из друзей
export const removeFriend = createAsyncThunk(
  'friends/removeFriend',
  async (friendId, { rejectWithValue, getState }) => {
    try {
      const response = await socialAPI.removeFriend(friendId);
      // Получаем ID текущего пользователя из state
      const currentUserId = getState().auth?.user?.id;
      return { 
        userId: currentUserId, // ID текущего пользователя (кто удаляет)
        friendId: friendId // ID удаляемого друга
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to remove friend');
    }
  }
);

// Async thunk для получения списка друзей
export const fetchFriends = createAsyncThunk(
  'friends/fetchFriends',
  async ({ userId, limit = 20, offset = 0 } = {}, { rejectWithValue }) => {
    try {
      const response = await socialAPI.getFriends(userId, { limit, offset });
      
      // Проверяем статус ответа
      if (response.status >= 400) {
        return rejectWithValue(response.data?.error || 'Failed to fetch friends');
      }
      
      // Проверяем, что response.data существует
      if (!response || !response.data) {
        return { userId, friends: [], hasMore: false };
      }
      
      // Обрабатываем разные форматы ответа
      let friends = [];
      if (Array.isArray(response.data.friends)) {
        friends = response.data.friends;
      } else if (Array.isArray(response.data)) {
        friends = response.data;
      }
      
      const hasMore = response.data?.has_more !== false && friends.length === limit;
      return { userId, friends, hasMore };
    } catch (error) {
      console.error('fetchFriends error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to fetch friends';
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk для получения входящих запросов
export const fetchIncomingRequests = createAsyncThunk(
  'friends/fetchIncomingRequests',
  async ({ limit = 20, offset = 0 } = {}, { rejectWithValue }) => {
    try {
      const response = await socialAPI.getIncomingFriendRequests({ limit, offset });
      
      // Проверяем, что response.data существует
      if (!response || !response.data) {
        return { requests: [], hasMore: false };
      }
      
      // Обрабатываем разные форматы ответа
      let requests = [];
      if (Array.isArray(response.data.requests)) {
        requests = response.data.requests;
      } else if (Array.isArray(response.data)) {
        requests = response.data;
      }
      
      const hasMore = response.data?.has_more !== false && requests.length === limit;
      return { requests, hasMore };
    } catch (error) {
      console.error('fetchIncomingRequests error:', error);
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to fetch incoming requests');
    }
  }
);

// Async thunk для получения исходящих запросов
export const fetchOutgoingRequests = createAsyncThunk(
  'friends/fetchOutgoingRequests',
  async ({ limit = 20, offset = 0 } = {}, { rejectWithValue }) => {
    try {
      const response = await socialAPI.getOutgoingFriendRequests({ limit, offset });
      
      // Проверяем, что response.data существует
      if (!response || !response.data) {
        return { requests: [], hasMore: false };
      }
      
      // Обрабатываем разные форматы ответа
      let requests = [];
      if (Array.isArray(response.data.requests)) {
        requests = response.data.requests;
      } else if (Array.isArray(response.data)) {
        requests = response.data;
      }
      
      const hasMore = response.data?.has_more !== false && requests.length === limit;
      return { requests, hasMore };
    } catch (error) {
      console.error('fetchOutgoingRequests error:', error);
      return rejectWithValue(error.response?.data?.error || error.message || 'Failed to fetch outgoing requests');
    }
  }
);

// Async thunk для проверки статуса дружбы
export const checkFriendshipStatus = createAsyncThunk(
  'friends/checkFriendshipStatus',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await socialAPI.getFriendshipStatus(userId);
      return { userId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to check friendship status');
    }
  }
);

const friendsSlice = createSlice({
  name: 'friends',
  initialState: {
    // Список друзей по пользователям: { userId: { friends: [], hasMore: boolean, isLoading: boolean } }
    friends: {},
    // Входящие запросы в друзья
    incomingRequests: [],
    // Исходящие запросы в друзья
    outgoingRequests: [],
    // Статус дружбы с пользователями: { userId: 'none' | 'pending_incoming' | 'pending_outgoing' | 'friends' }
    friendshipStatus: {},
    // Общее состояние
    isLoading: false,
    error: null,
    hasMoreIncoming: true,
    hasMoreOutgoing: true,
  },
  reducers: {
    // Обновление статуса дружбы (приходит через WebSocket)
    updateFriendshipStatus: (state, action) => {
      const { userId, status } = action.payload;
      state.friendshipStatus[userId] = status;
    },

    // Добавление нового запроса в друзья (приходит через WebSocket)
    addIncomingRequest: (state, action) => {
      const request = action.payload;
      
      // Проверяем, нет ли уже такого запроса
      const exists = state.incomingRequests.find(r => r.id === request.id);
      if (!exists) {
        state.incomingRequests.unshift(request);
        // Обновляем статус дружбы по requester_id
        const requesterId = request.requester_id || request.requester?.id;
        if (requesterId) {
          state.friendshipStatus[requesterId] = 'pending_incoming';
        }
      }
    },

    // Удаление запроса в друзья (приходит через WebSocket)
    removeFriendRequest: (state, action) => {
      const { requestId, userId, requesterId, addresseeId } = action.payload;
      
      // Удаляем из входящих запросов по requestId или requesterId
      if (requestId) {
        state.incomingRequests = state.incomingRequests.filter(r => r.id !== requestId);
      }
      if (requesterId) {
        // Удаляем по requester_id (для случая, когда отклоняется запрос)
        state.incomingRequests = state.incomingRequests.filter(r => {
          const reqId = r.requester_id || r.requester?.id || r.fromUserId;
          return reqId !== requesterId;
        });
      }
      
      // Удаляем из исходящих запросов
      if (requestId) {
        state.outgoingRequests = state.outgoingRequests.filter(r => r.id !== requestId);
      }
      if (addresseeId) {
        // Удаляем по addressee_id (для случая, когда отклоняется исходящий запрос)
        state.outgoingRequests = state.outgoingRequests.filter(r => {
          const addrId = r.addressee_id || r.addressee?.id || r.toUserId;
          return addrId !== addresseeId;
        });
      }
      if (userId) {
        // userId здесь это requester_id (пользователь, отправивший запрос)
        // Удаляем запрос, где requester_id === userId (это исходящий запрос от текущего пользователя)
        state.outgoingRequests = state.outgoingRequests.filter(r => {
          const reqId = r.requester_id || r.requester?.id;
          return reqId !== userId;
        });
      }
      
      // Обновляем статус дружбы
      if (userId) {
        state.friendshipStatus[userId] = 'none';
      }
      if (requesterId) {
        state.friendshipStatus[requesterId] = 'none';
      }
      if (addresseeId) {
        state.friendshipStatus[addresseeId] = 'none';
      }
    },

    // Добавление нового друга (приходит через WebSocket)
    addFriend: (state, action) => {
      const { userId, friend } = action.payload;
      
      // Обновляем статус дружбы
      state.friendshipStatus[userId] = 'friends';
      state.friendshipStatus[friend.id] = 'friends';
      
      // Добавляем в список друзей
      if (!state.friends[userId]) {
        state.friends[userId] = { friends: [], hasMore: true, isLoading: false };
      }
      
      const exists = state.friends[userId].friends.find(f => f.id === friend.id);
      if (!exists) {
        state.friends[userId].friends.push(friend);
      }
      
      // Удаляем из запросов
      state.incomingRequests = state.incomingRequests.filter(r => r.fromUserId !== userId);
      state.outgoingRequests = state.outgoingRequests.filter(r => r.toUserId !== userId);
    },

    // Удаление друга (приходит через WebSocket)
    removeFriendFromList: (state, action) => {
      const { userId, friendId } = action.payload;
      
      // Обновляем статус дружбы для обоих пользователей
      if (userId) {
        state.friendshipStatus[userId] = 'none';
      }
      if (friendId) {
        state.friendshipStatus[friendId] = 'none';
      }
      
      // Удаляем из списка друзей для обоих пользователей
      if (userId && state.friends[userId]) {
        state.friends[userId].friends = state.friends[userId].friends.filter(
          f => f.id !== friendId
        );
      }
      
      // Также удаляем из списка друзей удаляемого друга
      if (friendId && state.friends[friendId]) {
        state.friends[friendId].friends = state.friends[friendId].friends.filter(
          f => f.id !== userId
        );
      }
    },

    // Оптимистичное обновление статуса дружбы
    updateFriendshipStatusOptimistic: (state, action) => {
      const { userId, status } = action.payload;
      state.friendshipStatus[userId] = status;
    },

    // Очистка ошибки
    clearError: (state) => {
      state.error = null;
    },

    // Сброс состояния
    resetFriendsState: (state) => {
      state.friends = {};
      state.incomingRequests = [];
      state.outgoingRequests = [];
      state.friendshipStatus = {};
      state.isLoading = false;
      state.error = null;
      state.hasMoreIncoming = true;
      state.hasMoreOutgoing = true;
    },
  },
  extraReducers: (builder) => {
    builder
      // Отправка запроса в друзья
      .addCase(sendFriendRequest.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
          .addCase(sendFriendRequest.fulfilled, (state, action) => {
            state.isLoading = false;
            const request = action.payload;
            
            // Добавляем в исходящие запросы
            if (request && request.addressee_id) {
              state.outgoingRequests.unshift(request);
              state.friendshipStatus[request.addressee_id] = 'pending_outgoing';
            }
          })
          .addCase(sendFriendRequest.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
          })
          
          // Отмена запроса в друзья
          .addCase(cancelFriendRequest.pending, (state) => {
            state.isLoading = true;
            state.error = null;
          })
          .addCase(cancelFriendRequest.fulfilled, (state, action) => {
            state.isLoading = false;
            const { userId } = action.payload;
            
        // Удаляем из исходящих запросов по addressee_id
        state.outgoingRequests = state.outgoingRequests.filter(r => {
          const addrId = r.addressee?.id || r.addressee_id || r.toUserId;
          return addrId !== userId;
        });
            
            // Обновляем статус дружбы
            state.friendshipStatus[userId] = 'none';
          })
          .addCase(cancelFriendRequest.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
          })
      
      // Принятие запроса в друзья
      .addCase(acceptFriendRequest.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(acceptFriendRequest.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload;
        const requesterId = payload?.userId || action.meta.arg; // ID пользователя, отправившего запрос
        const currentUserId = payload?.currentUserId; // ID текущего пользователя (который принял запрос)
        
        // Обновляем статус дружбы для обоих пользователей
        if (requesterId) {
          state.friendshipStatus[requesterId] = 'friends';
        }
        
        // Если бэкенд вернул данные о друге (пользователе, отправившем запрос), добавляем его в список друзей текущего пользователя
        const friend = payload?.friend;
        if (friend && friend.id && currentUserId) {
          state.friendshipStatus[friend.id] = 'friends';
          
          // Добавляем в список друзей текущего пользователя
          if (!state.friends[currentUserId]) {
            state.friends[currentUserId] = { friends: [], hasMore: true, isLoading: false };
          }
          
          const exists = state.friends[currentUserId].friends.find(f => f.id === friend.id);
          if (!exists) {
            state.friends[currentUserId].friends.push(friend);
          }
        }
        
        // Если бэкенд вернул данные о текущем пользователе (который принял запрос), добавляем его в список друзей requesterId
        // Это нужно для обновления списка друзей у пользователя, отправившего запрос
        const currentUserAsFriend = payload?.current_user;
        if (currentUserAsFriend && currentUserAsFriend.id && requesterId) {
          if (!state.friends[requesterId]) {
            state.friends[requesterId] = { friends: [], hasMore: true, isLoading: false };
          }
          
          const exists = state.friends[requesterId].friends.find(f => f.id === currentUserAsFriend.id);
          if (!exists) {
            state.friends[requesterId].friends.push(currentUserAsFriend);
          }
        }
        
        // Удаляем из входящих запросов
        state.incomingRequests = state.incomingRequests.filter(r => {
          const reqId = r.requester?.id || r.requester_id;
          return reqId !== requesterId;
        });
        
        // Удаляем из исходящих запросов (если запрос был в исходящих)
        // requesterId - это пользователь, отправивший запрос, который был принят
        // Нужно удалить запрос, где requester_id === requesterId (это исходящий запрос от requesterId)
        state.outgoingRequests = state.outgoingRequests.filter(r => {
          const reqId = r.requester?.id || r.requester_id;
          return reqId !== requesterId;
        });
      })
      .addCase(acceptFriendRequest.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Отклонение запроса в друзья
      .addCase(rejectFriendRequest.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(rejectFriendRequest.fulfilled, (state, action) => {
        state.isLoading = false;
        const { userId } = action.payload; // userId здесь - это requester_id (пользователь, отправивший запрос)
        
        // Удаляем из входящих запросов по requester_id
        state.incomingRequests = state.incomingRequests.filter(r => {
          const reqId = r.requester_id || r.requester?.id || r.fromUserId;
          return reqId !== userId;
        });
        
        // Обновляем статус дружбы для requester_id (userId)
        // Это нужно для обновления FriendButton на странице профиля requester для addressee
        state.friendshipStatus[userId] = 'none';
        
        // Также обновляем статус для текущего пользователя (addressee), если он есть в списке
        // Это нужно для обновления FriendButton на странице профиля addressee
        // Но мы не знаем текущего пользователя здесь, поэтому обновим через WebSocket
      })
      .addCase(rejectFriendRequest.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Удаление из друзей
      .addCase(removeFriend.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(removeFriend.fulfilled, (state, action) => {
        state.isLoading = false;
        const { userId, friendId } = action.payload;
        
        if (!userId || !friendId) {
          console.error('removeFriend.fulfilled: missing userId or friendId', action.payload);
          return;
        }
        
        // Обновляем статус дружбы для обоих пользователей
        state.friendshipStatus[userId] = 'none';
        state.friendshipStatus[friendId] = 'none';
        
        // Удаляем из списка друзей текущего пользователя (userId - это тот, кто удаляет)
        if (state.friends[userId]) {
          state.friends[userId].friends = state.friends[userId].friends.filter(
            f => f.id !== friendId
          );
        }
        
        // Также удаляем из списка друзей удаляемого друга (friendId - это удаляемый друг)
        // Это нужно, чтобы если мы смотрим профиль удаляемого друга, он тоже исчез из его списка
        if (state.friends[friendId]) {
          state.friends[friendId].friends = state.friends[friendId].friends.filter(
            f => f.id !== userId
          );
        }
      })
      .addCase(removeFriend.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Получение списка друзей
      .addCase(fetchFriends.pending, (state, action) => {
        const { userId } = action.meta.arg;
        
        if (!state.friends[userId]) {
          state.friends[userId] = { friends: [], hasMore: true, isLoading: false };
        }
        state.friends[userId].isLoading = true;
      })
      .addCase(fetchFriends.fulfilled, (state, action) => {
        const { userId, friends, hasMore } = action.payload;
        
        // Проверяем, что friends - это массив
        if (!Array.isArray(friends)) {
          console.warn('fetchFriends: friends is not an array', friends);
          if (state.friends[userId]) {
            state.friends[userId].isLoading = false;
          }
          return;
        }
        
        state.friends[userId] = { friends, hasMore, isLoading: false };
      })
      .addCase(fetchFriends.rejected, (state, action) => {
        const { userId } = action.meta.arg;
        
        if (state.friends[userId]) {
          state.friends[userId].isLoading = false;
        }
        state.error = action.payload;
      })
      
      // Получение входящих запросов
      .addCase(fetchIncomingRequests.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchIncomingRequests.fulfilled, (state, action) => {
        state.isLoading = false;
        const { requests, hasMore } = action.payload;
        
        // Проверяем, что requests - это массив
        if (!Array.isArray(requests)) {
          console.warn('fetchIncomingRequests: requests is not an array', requests);
          return;
        }
        
        // Если это первая загрузка, заменяем список
        if (state.incomingRequests.length === 0 || action.meta.arg?.offset === 0) {
          state.incomingRequests = requests;
        } else {
          // Иначе добавляем новые запросы
          const existingIds = new Set(state.incomingRequests.map(r => r.id || r.requester?.id));
          const newRequests = requests.filter(r => {
            const id = r.id || r.requester?.id;
            return id && !existingIds.has(id);
          });
          state.incomingRequests = [...state.incomingRequests, ...newRequests];
        }
        
        state.hasMoreIncoming = hasMore;
        
        // Обновляем статусы дружбы
        if (Array.isArray(requests)) {
          requests.forEach(request => {
            const requesterId = request.requester?.id || request.requester_id;
            if (requesterId) {
              state.friendshipStatus[requesterId] = 'pending_incoming';
            }
          });
        }
      })
      .addCase(fetchIncomingRequests.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Получение исходящих запросов
      .addCase(fetchOutgoingRequests.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOutgoingRequests.fulfilled, (state, action) => {
        state.isLoading = false;
        const { requests, hasMore } = action.payload;
        
        // Проверяем, что requests - это массив
        if (!Array.isArray(requests)) {
          console.warn('fetchOutgoingRequests: requests is not an array', requests);
          return;
        }
        
        // Если это первая загрузка, заменяем список
        if (state.outgoingRequests.length === 0 || action.meta.arg?.offset === 0) {
          state.outgoingRequests = requests;
        } else {
          // Иначе добавляем новые запросы
          const existingIds = new Set(state.outgoingRequests.map(r => r.id || r.addressee?.id));
          const newRequests = requests.filter(r => {
            const id = r.id || r.addressee?.id;
            return id && !existingIds.has(id);
          });
          state.outgoingRequests = [...state.outgoingRequests, ...newRequests];
        }
        
        state.hasMoreOutgoing = hasMore;
        
        // Обновляем статусы дружбы
        if (Array.isArray(requests)) {
          requests.forEach(request => {
            const addresseeId = request.addressee?.id || request.addressee_id;
            if (addresseeId) {
              state.friendshipStatus[addresseeId] = 'pending_outgoing';
            }
          });
        }
      })
      .addCase(fetchOutgoingRequests.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Проверка статуса дружбы
      .addCase(checkFriendshipStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkFriendshipStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        const { userId, status, is_requester } = action.payload;
        if (userId) {
          // Преобразуем статус в формат, используемый в state
          let friendshipStatus = 'none';
          if (status === 'accepted') {
            friendshipStatus = 'friends';
          } else if (status === 'pending') {
            friendshipStatus = is_requester ? 'pending_outgoing' : 'pending_incoming';
          } else if (status === 'blocked') {
            friendshipStatus = 'blocked';
          }
          state.friendshipStatus[userId] = friendshipStatus;
        }
      })
      .addCase(checkFriendshipStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

// Экспортируем actions
export const {
  updateFriendshipStatus,
  addIncomingRequest,
  removeFriendRequest,
  addFriend,
  removeFriendFromList,
  updateFriendshipStatusOptimistic,
  clearError,
  resetFriendsState,
} = friendsSlice.actions;

// Селекторы
export const selectFriends = (state, userId) => {
  return state.friends.friends[userId] || { friends: [], hasMore: true, isLoading: false };
};
export const selectIncomingRequests = (state) => state.friends.incomingRequests;
export const selectOutgoingRequests = (state) => state.friends.outgoingRequests;
export const selectFriendshipStatus = (state, userId) => state.friends.friendshipStatus[userId] || 'none';
export const selectFriendsLoading = (state) => state.friends.isLoading;
export const selectFriendsError = (state) => state.friends.error;
export const selectHasMoreIncomingRequests = (state) => state.friends.hasMoreIncoming;
export const selectHasMoreOutgoingRequests = (state) => state.friends.hasMoreOutgoing;

export default friendsSlice.reducer;
